import { AbstractControl, AsyncValidatorFn } from '@angular/forms';
import { map, of, switchMap, timer } from 'rxjs';
import { MedicalComponentService, ValueSetHttpService } from 'shared/domain';

import { ComponentCodeErrorCode, ComponentType, ValidationResult } from '@treetop/shared/model';

const specialCharacters = '_-';
const allowedCharacters = `a-zA-Z0-9${specialCharacters}`;

const REGEX_TT_CODE = new RegExp(`^GL-[${allowedCharacters}]+$`);

export function validate(response: [boolean, ValidationResult<ComponentCodeErrorCode>]) {
  const exists = response[0];
  const valid = response[1];
  if (exists) {
    return { exists: true };
  }

  if (!valid || !valid.isValid) {
    return { invalid: ComponentCodeErrorCode[valid.errorCode] };
  }

  return null;
}

export function codeUtil(
  medicalComponentService: MedicalComponentService,
  valueSetService: ValueSetHttpService,
  type: ComponentType,
): AsyncValidatorFn {
  return (control: AbstractControl) => {
    return timer(300).pipe(
      switchMap(() => {
        const value = control.value as string;
        if (!value) {
          return of(null);
        }
        if (value.includes('VSV')) {
          return valueSetService.validateCodeableConceptValueCode(value).pipe(map((response) => validate(response)));
        } else if (value.includes('VS')) {
          return valueSetService.validateCodeableConceptCode(value).pipe(map((response) => validate(response)));
        } else {
          return medicalComponentService.validateTreetopCode(value, type).pipe(map((response) => validate(response)));
        }
      }),
    );
  };
}

export function isTTCodeInvalidViaRegex(code: string): boolean {
  return !REGEX_TT_CODE.test(code);
}

export const sanitizeInput = (input: string): string => {
  const regex = new RegExp(`[^${allowedCharacters}]+`, 'g');
  return input.replace(regex, '-');
};

export const getTTCodeFromTitle = (title: string) => {
  title = title ? title.trim().toLowerCase() : '';

  // Replace special German characters and then sanitize the result
  const sanitizedTitle = title.replace(/ü/g, 'ue').replace(/ö/g, 'oe').replace(/ä/g, 'ae').replace(/ß/g, 'ss');

  return `GL-${sanitizeInput(sanitizedTitle)}`;
};
